import {
  DistanceIcon,
  DurationIcon,
  ExternalMediaList,
  Ride,
  RouteService,
  VerticalGainIcon,
  cyclingProfiles,
  difficultiesMap,
  rideThemesMap,
  useUnits,
} from '@geovelo-frontends/commons';
import { Close, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  SvgIconProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { saveAs } from 'file-saver';
import { Link } from 'gatsby';
import React, { MouseEvent, ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PrismicLinkType } from '../../graphql-types';
import communityImg from '../assets/images/community.svg';
import { AppContext } from '../context';
import PrivacyDialog, { privaciesMap } from '../templates/ride-details/privacy-dialog';

import Button from './button';
import { DownloadIcon, EditIcon } from './icons';
import CTA from './slices/cta';

function RideRecap({
  disableGPXDownload,
  cta_link,
  cta_title,
  isOwner,
  ride,
  setRide,
}: {
  cta_title?: { text?: string | null };
  cta_link?: { link_type?: PrismicLinkType | null; url?: string | null } | null;
  disableGPXDownload?: boolean;
  isOwner?: boolean;
  ride?: Ride;
  setRide?: (ride: Ride) => void;
}) {
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { toTime, toDistance } = useUnits();
  const [downloading, setDownloading] = useState(false);
  const [privacyDialogOpen, openPrivacyDialog] = useState(false);
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [difficultyInfoDialogOpen, openDifficultyInfoDialog] = useState(false);
  const [publicMessageHidden, hidePublicMessage] = useState(false);
  const {
    ride: { themes },
  } = useContext(AppContext);

  async function handleGPXDownload(getReturnGPX = false) {
    if (!ride || (!getReturnGPX && !ride.routeId) || (getReturnGPX && !ride.returnRouteId)) return;

    setDownloading(true);

    try {
      let blob;
      if (!getReturnGPX && ride.routeId) blob = await RouteService.getGPX(ride.routeId);
      if (getReturnGPX && ride.returnRouteId) blob = await RouteService.getGPX(ride.returnRouteId);

      if (blob)
        saveAs(
          blob,
          `${(
            ride.title ||
            t(
              getReturnGPX
                ? 'geovelo.rides.list_item.return_title'
                : 'geovelo.rides.list_item.default_title',
              { id: ride.id },
            ) ||
            ''
          ).replace(/ /g, '_')}.gpx`,
        );
    } catch (err) {
      console.error(err);
    }

    setDownloading(false);
  }

  const rideThemes =
    ride &&
    themes
      ?.filter(({ id }) => ride.themeIds.includes(id))
      .map(({ key }) => rideThemesMap[key])
      .filter(Boolean);
  const privacy =
    ride && ride.privacy && ride.privacy !== 'forcePrivate' && privaciesMap[ride.privacy];

  return (
    <>
      <Card sx={{ borderRadius: 4 }} variant="outlined">
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box alignItems="center" display="flex" gap={2}>
            <Stat
              Icon={DurationIcon}
              value={ride ? toTime(ride.overloadDuration || ride.duration || 0) : undefined}
            />
            <Stat Icon={DistanceIcon} value={ride ? toDistance(ride.distance || 0) : undefined} />
            <Stat
              Icon={VerticalGainIcon}
              value={ride ? toDistance(ride.verticalGain || 0) : undefined}
            />
          </Box>
          <Box display="flex" flexDirection="column" gap={1}>
            {(!ride || ride.profile) && (
              <InlineStat
                label={t('geovelo.rides.detail.type')}
                value={
                  ride?.profile
                    ? ['daily', 'direct', 'safe', 'touristic'].includes(ride.profile)
                      ? t('geovelo.rides.detail.bike_types.city_bike')
                      : t(cyclingProfiles[ride.profile]?.longLabelKey || '')
                    : undefined
                }
              />
            )}
            {(!rideThemes || rideThemes.length > 0) && (
              <InlineStat
                label={t('geovelo.rides.detail.categories')}
                value={
                  rideThemes ? rideThemes.map((theme) => t(theme.labelKey)).join(', ') : undefined
                }
              />
            )}
            {(ride === undefined || ride.difficulty) && (
              <InlineStat
                label={t('geovelo.rides.detail.level')}
                onInfoClick={() => openDifficultyInfoDialog(true)}
                value={ride ? t(difficultiesMap[ride.difficulty || 'medium'].labelKey) : undefined}
              />
            )}
            {ride && ride.medias.length > 0 && <ExternalMediaList medias={ride.medias} />}
            {isOwner && ride?.privacy && ride.privacy !== 'forcePrivate' && (
              <InlineStat
                label={t('geovelo.rides.detail.visibility')}
                value={
                  ride ? (
                    privacy && (
                      <Button
                        color="primary"
                        onClick={() => openPrivacyDialog(true)}
                        size="small"
                        startIcon={<privacy.Icon fontSize="small" />}
                      >
                        <Typography sx={{ textDecoration: 'underline' }}>
                          {t(privacy.labelKey)}
                        </Typography>
                      </Button>
                    )
                  ) : (
                    <Skeleton />
                  )
                }
              />
            )}
          </Box>
          {isOwner && ride?.privacy === 'private' && (
            <Collapse in={!publicMessageHidden}>
              <Box
                borderRadius={2}
                display="flex"
                flexDirection="column"
                gap={1}
                padding={2}
                sx={{ backgroundColor: '#f3f7fd' }}
              >
                <Box alignItems="flex-start" display="flex" justifyContent="space-between">
                  <img height={55} src={communityImg} />
                  <Tooltip title={t('commons.actions.close')}>
                    <IconButton onClick={() => hidePublicMessage(true)} size="small">
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2">{t('geovelo.rides.detail.switch_public')}</Typography>
                <Button
                  color="primary"
                  onClick={() => openPrivacyDialog(true)}
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  <Typography sx={{ textDecoration: 'underline' }} variant="body2">
                    {t('geovelo.rides.detail.actions.switch_public')}
                  </Typography>
                </Button>
              </Box>
            </Collapse>
          )}
        </CardContent>
        {(!disableGPXDownload || (Boolean(cta_title) && Boolean(cta_link))) && (
          <CardActions sx={{ justifyContent: 'space-between', padding: 2 }}>
            {!disableGPXDownload && (
              <Button
                color="primary"
                disabled={!ride || downloading}
                onClick={({ currentTarget }: MouseEvent<HTMLButtonElement>) =>
                  ride?.returnRouteId ? setDownloadMenuAnchorEl(currentTarget) : handleGPXDownload()
                }
                startIcon={<DownloadIcon />}
                variant="contained"
              >
                {t('commons.actions.download')} (.gpx)
              </Button>
            )}
            {!!cta_title && !!cta_link && <CTA link={cta_link} title={cta_title} variant="Text" />}
            <Menu
              anchorEl={downloadMenuAnchorEl}
              id={`download-menu`}
              onClose={() => setDownloadMenuAnchorEl(null)}
              open={Boolean(downloadMenuAnchorEl)}
            >
              <MenuItem
                dense
                onClick={() => {
                  handleGPXDownload();
                  setDownloadMenuAnchorEl(null);
                }}
              >
                {ride?.from?.address + ' ➔ ' + ride?.to?.address}
              </MenuItem>
              {!!ride?.returnRouteId && (
                <MenuItem
                  dense
                  onClick={() => {
                    handleGPXDownload(true);
                    setDownloadMenuAnchorEl(null);
                  }}
                >
                  {ride?.to?.address + ' ➔ ' + ride?.from?.address}
                </MenuItem>
              )}
            </Menu>
            {isOwner && ride && (
              <Button
                color="primary"
                component={Link}
                startIcon={<EditIcon />}
                to={`/${currentLanguage}/ride-update/${ride.id}`}
                variant="contained"
              >
                {t('commons.actions.edit')}
              </Button>
            )}
          </CardActions>
        )}
      </Card>
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => openDifficultyInfoDialog(false)}
        open={difficultyInfoDialogOpen}
      >
        <DialogTitle>{t('geovelo.rides.detail.levels.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography>{t('geovelo.rides.detail.levels.description')}</Typography>
          <Typography component="ul">
            <li>{t('geovelo.rides.detail.levels.easy')}</li>
            <li>{t('geovelo.rides.detail.levels.medium')}</li>
            <li>{t('geovelo.rides.detail.levels.hard')}</li>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => openDifficultyInfoDialog(false)} size="small">
            {t('commons.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
      <PrivacyDialog
        onClose={(updatedRide) => {
          if (updatedRide) setRide?.(updatedRide);
          openPrivacyDialog(false);
        }}
        open={privacyDialogOpen}
        ride={ride}
      />
    </>
  );
}

function Stat({
  Icon,
  value,
}: {
  Icon: (props: SvgIconProps) => React.JSX.Element;
  value: ReactNode | undefined;
}): React.JSX.Element {
  return (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      gap={1}
      width="calc((100% - 32px) / 3)"
    >
      <Icon color="primary" />
      <Typography>
        {value !== undefined ? value : <Skeleton variant="text" width={80} />}
      </Typography>
    </Box>
  );
}

function InlineStat({
  label,
  value,
  onInfoClick,
}: {
  label: ReactNode;
  onInfoClick?: () => void;
  value: ReactNode;
}): React.JSX.Element {
  return (
    <Box alignItems="center" display="flex" gap={1}>
      <Typography>
        <Typography component="span" fontWeight={600} variant="body1">
          {label}
        </Typography>
        &nbsp;&nbsp;
        {value !== undefined ? (
          value
        ) : (
          <Skeleton sx={{ display: 'inline-block' }} variant="text" width={80} />
        )}
      </Typography>
      {onInfoClick && value !== undefined && (
        <IconButton onClick={onInfoClick} size="small" sx={{ padding: 0 }}>
          <InfoOutlined color="action" fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

export default RideRecap;
